import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDriftRefs } from '@/components/app/DriftUI';
import { isActiveRef } from '@/lib/driftApi';
import { readJson, writeJson } from './storage';

/*
  Demoprofil: hvilken (fiktiv) medarbeider «Min dag» og registreringene gjelder for i previewen.
  Dette er IKKE innlogging eller tilgangsstyring – bare en tydelig merket kontekst som følger med gjennom flyten,
  slik at den ansatte slipper å velge seg selv i hvert skjema. Lagres lokalt i nettleseren.
*/

const KEY = 'hr:demo-profile';
const ProfileContext = createContext(null);

export function DemoProfileProvider({ children }) {
  const { refs, loading, error, retry } = useDriftRefs();
  const [storedId, setStoredId] = useState(() => readJson(window.localStorage, KEY, null));

  const employees = useMemo(() => (refs?.employees ?? []).filter(isActiveRef), [refs]);
  // Arkivert eller ukjent lagret profil faller tilbake til første aktive medarbeider – aldri en tom kontekst.
  const employee = useMemo(() => employees.find((e) => e.id === storedId) ?? employees[0] ?? null, [employees, storedId]);

  useEffect(() => {
    if (employee && employee.id !== storedId) {
      writeJson(window.localStorage, KEY, employee.id);
      setStoredId(employee.id);
    }
  }, [employee, storedId]);

  const setEmployeeId = useCallback((id) => {
    writeJson(window.localStorage, KEY, id);
    setStoredId(id);
  }, []);

  const value = useMemo(
    () => ({ employee, employeeId: employee?.id ?? '', employees, setEmployeeId, loading: loading && !refs, error: !refs ? error : null, retry }),
    [employee, employees, setEmployeeId, loading, refs, error, retry],
  );
  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
}

export function useDemoProfile() {
  const ctx = useContext(ProfileContext);
  if (!ctx) throw new Error('useDemoProfile utenfor DemoProfileProvider');
  return ctx;
}
